import selectors from '../rootSelectors';
import { TRC, TRC_QUESTION_TYPES, TRO } from '../constants/measure';
import { TRCO_FETCH_RETRY } from '../common/api/apiRetryGetConstants';
import { TRO_PASSING_LEVEL_STATUSES } from '../constants/book';
import { LANTERN_ERROR_CODES, ERROR_STATUS } from '../constants/error';
import { WAITING_SOAPBOX_TIMEOUT_MS } from '../constants/time';
import { redirectToAR } from './troUtils';
import {
    resetSelectedIndex,
    resetSelectedMultiChoiceIndexes,
    selectMultipleResponses,
    selectPossibleResponse,
    selectMultiChoiceResponses,
    setClassifiedResponses,
    resetClassifiedResponses,
    setIsQuestionCompleted,
} from '../common/questions/questionsActions';
import * as TRCO from '../common/api/trcoActions';
import {
    enableGoBackButton,
    disableGoBackButton,
    activateNextButton,
    deactivateNextButton,
    blockScreen,
    unblockScreen,
    showHelpPopup,
    hideHelpPopup,
    hideSpinner,
    showSpinner,
    showPauseButton,
    setPauseButtonActive,
    setPauseButtonInactive,
    setShouldDisplayCaption,
} from '../common/screen/screenActions';
import {
    reportAppError,
    stopBackgroundRetry,
    setAudioSavingError,
    startBackgroundRetry,
    cancelBackgroundRetry,
} from '../common/api/apiCallActions';
import { setPlaylistItemCompleted } from '../common/playlist/playlistActions';
import { narrateStart, setCaption } from '../common/audio/audioActions';
import { setShouldPreventAlexAnimation } from '../common/alex/AlexActions';
import { playAvatarAudio, setAvatarInstructions } from '../common/avatar/avatarActions';
import { goToStartPage, goToBookStart, goToQuestion } from '../common/navigation/navigationActions';
import {
    clearRecordedResult,
    toggleIsVrResultProcessingTimeout,
} from '../common/recording/recordingActions';
import { getCurrentBookResult } from './trcSelectors';
import { hasPersistedRecording } from '../common/persistence/persistanceUtils';
import { syncPersistedRecording } from '../common/persistence/persistanceActions';
import TROServiceClient from '../common/clients/TROServiceClient';
import { TRO_DEMO_VIDEO_PLAYER_ID } from '../common/components/demoVideos/demoVideoConstants';
import { TRO_COMPREHENSION_SECTION, TRO_READING } from '../common/timeTracking/timeTrackingKeys';
import { VERIFICATION_STATUS } from './pages/instructions/TROInstructionsConstants';
import { getStudentResults } from '../common/api/resultStudentActions';
import { trackTroBookIsCompleted, trackTROBooksComplete, trackTroError } from './trcReporting';

export const SET_MEDIA = 'SET_MEDIA';
export const NEXT_PAGE = 'NEXT_PAGE';
export const RESET_PAGES_VIEWED = 'RESET_PAGES_VIEWED';
export const SET_COMPREHENSION_ATTEMPT = 'SET_COMPREHENSION_ATTEMPT';
export const INCREASE_COMPREHENSION_ATTEMPT = 'INCREASE_COMPREHENSION_ATTEMPT';
export const CLEAN_UP_BOOK = 'CLEAN_UP_BOOK';
export const PLAYLIST_PLAY = 'PLAYLIST_PLAY';
export const SET_TRC_CONTENT = 'SET_TRC_CONTENT';
export const RESET_ACTIVE_PAGE = 'RESET_ACTIVE_PAGE';
export const SET_JPLAYER_OPTION = 'SET_JPLAYER_OPTION';
export const SET_TRC_BOOK_RESULT = 'SET_TRC_BOOK_RESULT';
export const COMPLETE_STEP_INDEX = 'COMPLETE_STEP_INDEX';
export const SET_READING_COMPLETE = 'SET_READING_COMPLETE';
export const SET_LAST_RECORDED_PAGE = 'SET_LAST_RECORDED_PAGE';
export const SET_CURRENT_STEP_INDEX = 'SET_CURRENT_STEP_INDEX';
export const SET_PROGRESS_STEPS_COUNT = 'SET_PROGRESS_STEPS_COUNT';
export const UPDATE_SELECTED_COMPREHENSION_RESPONSES = 'UPDATE_SELECTED_COMPREHENSION_RESPONSES';
export const SET_COMPREHENSION_COMPLETE = 'SET_COMPREHENSION_COMPLETE';
export const SET_PASSED_MIC_READ_CHECK = 'SET_PASSED_MIC_READ_CHECK';
export const SET_ATTEMPTED_MIC_READ_CHECK = 'SET_ATTEMPTED_MIC_READ_CHECK';
export const SET_PASSED_MULTIPLE_CHOICE_READ_CHECK = 'SET_PASSED_MULTIPLE_CHOICE_READ_CHECK';
export const SET_ATTEMPTED_MULTIPLE_CHOICE_READ_CHECK = 'SET_ATTEMPTED_MULTIPLE_CHOICE_READ_CHECK';
export const SET_DID_PREVIOUSLY_ENABLE_MIC = 'SET_DID_PREVIOUSLY_ENABLE_MIC';

// 3.2KB is the min size SoapBox accepts for audio since it will be longer than 0.1 seconds.
const MIN_AUDIO_SIZE = 3200;

export const setTRCContent = (content) => ({
    type: SET_TRC_CONTENT,
    content,
});

export const setCompletedStepIndex = completedStepIndex => ({
    type: COMPLETE_STEP_INDEX,
    payload: completedStepIndex,
});

export const setProgressStepsCount = stepsCount => ({
    type: SET_PROGRESS_STEPS_COUNT,
    payload: stepsCount,
});

export const setCurrentStepIndex = currentStepIndex => ({
    type: SET_CURRENT_STEP_INDEX,
    payload: currentStepIndex,
});

export const playPlaylist = playlistIndex => ({
    type: PLAYLIST_PLAY,
    index: playlistIndex,
    id: TRO_DEMO_VIDEO_PLAYER_ID,
});

export const goToNextPage = (incrementation = 1) => ({
    type: NEXT_PAGE,
    incrementation,
});

export const resetPagesViewed = () => ({
    type: RESET_PAGES_VIEWED,
});

export const setComprehensionAttempt = questionIndex => ({
    type: SET_COMPREHENSION_ATTEMPT,
    questionIndex,
});

export const increaseAttemptCount = questionIndex => ({
    type: INCREASE_COMPREHENSION_ATTEMPT,
    questionIndex,
});

export const cleanUpBook = () => ({
    type: CLEAN_UP_BOOK,
});

export const resetActivePage = () => ({
    type: RESET_ACTIVE_PAGE,
});

export const updateSelectedComprehensionResponses = (questionIndex, studentResponse) => ({
    type: UPDATE_SELECTED_COMPREHENSION_RESPONSES,
    questionIndex,
    studentResponse,
});

export const setTRCBookResult = bookResult => ({
    type: SET_TRC_BOOK_RESULT,
    bookResult,
});

export const setLastRecordedPage = () => ({
    type: SET_LAST_RECORDED_PAGE,
});

export const setReadingComplete = () => ({
    type: SET_READING_COMPLETE,
});

export const setComprehensionComplete = bookId => ({
    type: SET_COMPREHENSION_COMPLETE,
    bookId,
});

export const setPassedMicReadCheck = result => ({
    type: SET_PASSED_MIC_READ_CHECK,
    payload: result ,
});

export const setAttemptedMicReadCheck = () => ({
    type: SET_ATTEMPTED_MIC_READ_CHECK,
});

export const setPassedMultipleChoiceReadCheck = isPass => ({
    type: SET_PASSED_MULTIPLE_CHOICE_READ_CHECK,
    payload: { isPass },
});

export const setdidAttemptMultiChoiceReadingCheckCheck = () => ({
    type: SET_ATTEMPTED_MULTIPLE_CHOICE_READ_CHECK,
});

export const setPreviouslyEnabledMic = () => ({
    type: SET_DID_PREVIOUSLY_ENABLE_MIC,
});

export const saveTROComprehensionAndReadingTimeDuration = () => (dispatch, getStore) => {
    const store = getStore();
    const readingDuration = selectors.getTimeTrackingDuration(store, TRO_READING);
    const comprehensionDuration = selectors.getTimeTrackingDuration(store, TRO_COMPREHENSION_SECTION);
    const resultId = selectors.findResultFor(store, { measure: TRO.MEASURE, isComplete: false }).id;
    const bookId = selectors.getTRCBookId(store);

    const data = {
        book_id: bookId,
        reading_time_duration: readingDuration,
        comprehension_time_duration: comprehensionDuration,
    };

    return dispatch(TRCO.saveTimeDuration(data, resultId));
};

export const handleBookResultResponse = () => async (dispatch, getStore) => {
    const store = getStore();
    const vrScores = selectors.getVrScores(store);
    const allVrResultsHaveScores = selectors.getAllVrResultsHaveScores(store);
    const isVrResultInFailedState = selectors.getIsVrResultInFailedState(store);
    const shouldMoveToStartScreen = allVrResultsHaveScores && vrScores.length === 2;
    const shouldMoveToSecondBook = allVrResultsHaveScores && vrScores.length === 1;

    if (isVrResultInFailedState) {
        if (selectors.getExternalURLSourceStatus(store)) {
            const queryParams = {
                hasTroError: true,
                benchmarkMode: selectors.getisBenchmarkModeEnabled(store),
            };
            await dispatch(trackTroError('Voice Recording in failed state after completing reading'));
            redirectToAR(queryParams);
        } else {
            dispatch(showHelpPopup());
        }
    }
    if (shouldMoveToSecondBook) dispatch(goToBookStart(TRC));
    if (shouldMoveToStartScreen) {
        if (selectors.getExternalURLSourceStatus(store)){
            const queryParams = {
                hasTroError: false,
                benchmarkMode: selectors.getisBenchmarkModeEnabled(store),
            };
            redirectToAR(queryParams);
        } else {
            dispatch(goToStartPage());
            dispatch(setPlaylistItemCompleted(TRO.ASSESSMENT));
        }
    }
};

export const syncOfflineAudioForResult = (shouldRetry = false) =>  async (dispatch, getStore) => {
    const state = getStore();
    const currentResult = selectors.findResultFor(state, {
        measure: TRO.MEASURE,
        isComplete: false,
    });
    if (currentResult && await hasPersistedRecording(currentResult.id)) {
        return dispatch(syncPersistedRecording(currentResult.id, shouldRetry));
    }
    return Promise.resolve();
};

export const onQuestionChange = (questionIndex) => (dispatch, getStore) => {
    const state = getStore();
    const listOfSelectedResponses = selectors.getTRCSelectedResponses(state);
    const newSelectedResponse = listOfSelectedResponses[questionIndex];
    const questionItem = selectors.getTRCComprehensionItem(state, questionIndex);
    const isQuestionCompleted = selectors.getIsQuestionCompleted(state, questionIndex);
    const questionType = questionItem && questionItem.type;
    if (typeof newSelectedResponse !== 'undefined') {
        switch (questionType) {
        case TRC_QUESTION_TYPES.CLOZE_DROPDOWN:
        case TRC_QUESTION_TYPES.MULTI_CHOICE:
            dispatch(selectPossibleResponse(newSelectedResponse));
            dispatch(resetSelectedMultiChoiceIndexes());
            dispatch(resetClassifiedResponses());
            break;
        case TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS:
            dispatch(selectMultipleResponses(newSelectedResponse));
            dispatch(resetSelectedIndex());
            dispatch(resetClassifiedResponses());
            break;
        case TRC_QUESTION_TYPES.CLASSIFICATION:
            dispatch(setClassifiedResponses(newSelectedResponse));
            dispatch(resetSelectedMultiChoiceIndexes());
            dispatch(resetSelectedIndex());
            break;
        default:
            break;
        }
    } else {
        dispatch(resetSelectedIndex());
        dispatch(resetSelectedMultiChoiceIndexes());
        dispatch(resetClassifiedResponses());
        dispatch(deactivateNextButton());
    }
    if (isQuestionCompleted) {
        dispatch(activateNextButton());
    } else {
        dispatch(deactivateNextButton());
    }
    return (questionIndex === 0)
        ? dispatch(disableGoBackButton())
        : dispatch(enableGoBackButton());
};

export const saveSelectedResponse = (responseIndex, questionIndex, questionType) => (dispatch, getStore) => {
    const state = getStore();
    let selectedResponse = responseIndex;
    if (questionType === TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS) {
        selectedResponse = selectors.getMultiChoiceResponses(state);
    }
    dispatch(updateSelectedComprehensionResponses(questionIndex, selectedResponse));
};

export const onSelectAnswer = (responseIndex, questionIndex, questionType,
    maxPossibleResponses) => (dispatch) => {
    if (questionType === TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS) {
        dispatch(selectMultiChoiceResponses(
            responseIndex,
            maxPossibleResponses,
            questionIndex,
        ));
    } else {
        dispatch(setIsQuestionCompleted(questionIndex, responseIndex !== undefined));
        dispatch(activateNextButton());
        dispatch(selectPossibleResponse(responseIndex));
    }

    dispatch(saveSelectedResponse(responseIndex, questionIndex, questionType,
        maxPossibleResponses));
};

export const fetchTRCContent = () => (dispatch, getStore) => {
    const state = getStore();
    const isCurrentBookDone = selectors.getIsCurrentBookDone(state);
    let config;
    if (isCurrentBookDone) {
        const relativeLevel = selectors.getNextTROBookLevel(state);
        const gradeLevel = selectors.getGrade(state);
        const period = selectors.getToy(state);
        config = {gradeLevel, period, relativeLevel};
    }
    else {
        const bookId = selectors.getCurrentBookId(state);
        config = { id: bookId };
    }
    return dispatch(TRCO.getBook(config, TRCO_FETCH_RETRY))
        .then((book) => {
            dispatch(setTRCContent(book));
        });
};

export const saveBookReadingResponse = (resultId) => (dispatch) => dispatch(TRCO.addBookResult(resultId));

export const createTRCResult = () => (dispatch, getStore) => {
    const state = getStore();
    const result = selectors.findResultFor(state, { measure: TRO.MEASURE });
    const bookResult = selectors.getLatestBookResult(state);

    // First and Second book resume case
    if (result && bookResult.passingLevel !== TRO_PASSING_LEVEL_STATUSES.NOT_STARTED) {
        return Promise.resolve(result.id);
    }

    // Second book start case
    if (result && bookResult.passingLevel === TRO_PASSING_LEVEL_STATUSES.NOT_STARTED) {
        return dispatch(saveBookReadingResponse(result.id));
    }

    return dispatch(TRCO.createResult()).then(resultId => dispatch(saveBookReadingResponse(resultId)));
};

export const saveComprehensionResponse = (questionIndex, isLast=false) => (dispatch, getStore) => {
    const state = getStore();
    const duration = selectors.getQuestionTimeTrackingDuration(state);
    const response = selectors.getTRCComprehensionResult(state, questionIndex, duration);

    return dispatch(TRCO.saveComprehensionResponse(response, isLast));
};

export const narrateScreen = (avatarAudios, caption = '', preventAlexAnimate = false) =>
    (dispatch, getStore) => {
        const state = getStore();
        const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(state);
        const shouldShowPauseButton = selectors.getIsPaused(state);
        const isPauseButtonActive = selectors.getIsPauseButtonActive(state);
        const shouldDisplayCaption = selectors.getShouldDisplayCaption(state);
        if (preventAlexAnimate) {
            dispatch(setShouldPreventAlexAnimation(true));
        }
        if (avatarAudios) {
            if (isExternalURLSourcePresent) {
                if (!shouldShowPauseButton) {
                    dispatch(showPauseButton());
                }
                if (!isPauseButtonActive) {
                    dispatch(setPauseButtonActive());
                }
                if (!shouldDisplayCaption) {
                    dispatch(setCaption(caption));
                    dispatch(setShouldDisplayCaption(true));
                }
            }
            dispatch(blockScreen());
            dispatch(narrateStart());
            dispatch(setAvatarInstructions(avatarAudios));
            return dispatch(playAvatarAudio(avatarAudios))
                .then(() => {
                    dispatch(unblockScreen());
                    if (isExternalURLSourcePresent) {
                        dispatch(setPauseButtonInactive());
                        dispatch(setShouldDisplayCaption(false));
                    }
                });
        }
        return Promise.resolve();
    };

export const saveRecording = () => async (dispatch, getStore) => {
    const state = getStore();
    let encodedWav;
    try {
        encodedWav = await selectors.getEncodedWavResult(state);
    } catch (error) {
        dispatch(reportAppError(error.message));
        throw error;
    }
    const isReadingComplete = selectors.getIsReadingComplete(state);
    const isRecordingSavingInProgress = selectors.getIsRecordingSavingInProgress(state);

    if (encodedWav.length <= MIN_AUDIO_SIZE) {
        dispatch(setAudioSavingError({statusCode: LANTERN_ERROR_CODES.AUDIO_SIZE_THRESHOLD_NOT_MET}));
        dispatch(hideSpinner());
        dispatch(reportAppError(ERROR_STATUS.AUDIO_SIZE_THRESHOLD_NOT_MET));
        dispatch(clearRecordedResult());
        return Promise.reject();
    }

    if (isReadingComplete && isRecordingSavingInProgress) {
        dispatch(hideSpinner());
        dispatch(TRCO.saveStudentRecordingWithRetry());
        dispatch(goToQuestion(TRC, null, 1));
        return Promise.resolve();
    }

    return dispatch(TRCO.saveStudentRecordingWithRetry());
};

/**
 * Function `completeTRCBookResult` was intended to handle vrResult with [ready, pending, failed] statuses.
 * See examples of the behavior:
 * ```
 * @example 1: flow: ready
 * should move to second book or start page
 * ```
 * @example 2: flow: pending more then 2 minutes
 * - should show "waiting popup" 2 minutes, after that show another "result processing timeout popup"
 * - make retryable requests for the result with such intervals, 30, 25, 20, 15, 10, 5 seconds
 * ```
 * @example 3: flow: timeout
 * - should show waiting popup 2 minutes, after that show another "result processing timeout popup"
 * - dont make retryable requests for the result as "timeout" will last next 15 minutes
 * ```
 * @example 4: flow: pending => timeout
 * - should show waiting popup 2 minutes, after that show another "result processing timeout popup"
 * - stop make retryable requests when "timeout" status fetched, as "timeout" will last next 15 minutes
 * ```
 * @example 5: flow: pending => failed
 * - should show "waiting popup" till get "failed" status, then show "should invalidate" result help popup
 * - stop make retryable requests when "failed" status fetched
 * ```
 * @example 6: flow: pending => ready
 * - should move to second book or start page
 * - stop make retryable requests when "ready" status fetched
 * @return {Promise} promise which resolves
 */
export const completeTRCBookResult = () => async (dispatch, getStore) => {
    if (selectors.getIsRecordingSaved(getStore()) === false) return Promise.reject();

    let timeoutId;
    const { id: resultId } = selectors.findResultFor(getStore(), { measure: TRO.MEASURE, isComplete: false });
    const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(getStore());
    const shouldSetResult = false;
    const bookIndex = selectors.getBookIndex(getStore());
    const results = await dispatch(TRCO.getStudentResults(shouldSetResult));
    const result = results.find(studentResult => studentResult.result_id === resultId);

    if (!result) return dispatch(showHelpPopup());

    dispatch(setTRCBookResult(getCurrentBookResult(result)));

    const isPending = selectors.getIsVrResultInPendingState(getStore());
    const isVrResultProcessing = selectors.getIsVrResultProcessing(getStore());

    if (isVrResultProcessing) {
        dispatch(showHelpPopup());
        timeoutId = setTimeout(() => {
            dispatch(toggleIsVrResultProcessingTimeout());
            dispatch(unblockScreen());
            dispatch(cancelBackgroundRetry());
        }, WAITING_SOAPBOX_TIMEOUT_MS);
    }

    if (isPending) {
        dispatch(startBackgroundRetry());

        return dispatch(TRCO.getResultWithRetry(resultId))
            .then(response => dispatch(setTRCBookResult(getCurrentBookResult(response))))
            .catch(() => dispatch(hideHelpPopup()))
            .finally(() => {
                dispatch(handleBookResultResponse());

                // we don't clear timeoutId when error_reason is timeout,
                // as we still show waiting popup that changes to timeout popup after 2 minutes
                const isTimeout = selectors.getIsVrResultErrorTimedOut(getStore());
                if (!isTimeout) {
                    clearTimeout(timeoutId);
                    dispatch(unblockScreen());
                    dispatch(stopBackgroundRetry());
                }
            });
    }

    dispatch(unblockScreen());
    dispatch(handleBookResultResponse());
    dispatch(trackTroBookIsCompleted());

    if (isExternalURLSourcePresent && bookIndex >= 1) {
        dispatch(trackTROBooksComplete());
    }
    return Promise.resolve();
};

export const getReadingCheckResult = () => async (dispatch, getStore) => {
    const state = getStore();

    const studentDetails = {
        student_uid: selectors.getUserId(state),
        period: selectors.getToy(state),
        school_year: selectors.getSchoolYear(state).toString(),
        inst_uid: selectors.getInstId(state, TRO.MEASURE).toString(),
    };

    let encodedWav;
    try {
        encodedWav = await selectors.getEncodedWavResult(state);
    } catch (error) {
        dispatch(reportAppError(error.message));
        throw error;
    }

    const target = 'I have a pet cat';
    const verificationType = 'reading_check';

    const payload = {
        studentDetails,
        target,
        encodedWav,
        verificationType,
    };

    dispatch(TRCO.setVerificationStatus(VERIFICATION_STATUS.PENDING));
    dispatch(showSpinner());

    dispatch(TROServiceClient.postVerification(payload))
        .then(data => {
            dispatch(setPassedMicReadCheck(data));
            dispatch(TRCO.setVerificationStatus(VERIFICATION_STATUS.COMPLETE));
        })
        .catch(() => {
            dispatch(TRCO.setVerificationStatus(VERIFICATION_STATUS.ERROR));
        })
        .finally(() => {
            dispatch(hideSpinner());
            dispatch(setAttemptedMicReadCheck());
        });
};

export const patchDiscontinueResult = () => async (dispatch, getStore) => {
    const state = getStore();
    const studentUid = selectors.getUserId(state);
    await dispatch(showSpinner());
    const resultInStore = selectors.findResultFor(getStore(), { measure: TRO.MEASURE, isComplete: false });
    const resultId = resultInStore && resultInStore.id ? resultInStore.id : await dispatch(TRCO.createResult());
    await dispatch(
        TROServiceClient.patchResult(
            resultId,
            TROServiceClient.RESULT_OPERATION.DISCONTINUE,
            {},
            studentUid,
        ),
    );
    await dispatch(getStudentResults());
    if (selectors.getExternalURLSourceStatus(state)) {
        const queryParams = {
            hasTroError: false,
            benchmarkMode: selectors.getisBenchmarkModeEnabled(state),
        };
        await dispatch(trackTroError('Multiple choice reading check failed: discontinue result'));
        redirectToAR(queryParams);
    }
    return dispatch(hideSpinner());
};
